import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ContentLayout } from "@/components/layout/ContentLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, MoveUp, MoveDown } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { deleteFileFromS3 } from "@/services/s3Service";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Slides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [slideToDelete, setSlideToDelete] = useState<{id: string | number, title: string} | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSlides();
    // eslint-disable-next-line
  }, []);

  async function fetchSlides() {
    setLoading(true);
    const { data, error } = await supabase
      .from("home_slides")
      .select("id, title, storage_path, order_num")
      .order("order_num", { ascending: true });
    if (error) {
      toast({ title: "Hata", description: "Slaytlar yüklenemedi.", variant: "destructive" });
    } else {
      setSlides(data || []);
    }
    setLoading(false);
  }

  const moveSlide = async (id, direction) => {
    const index = slides.findIndex((slide) => slide.id === id);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === slides.length - 1)
    ) {
      return;
    }
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    const updatedSlides = [...slides];
    [updatedSlides[index], updatedSlides[swapIndex]] = [updatedSlides[swapIndex], updatedSlides[index]];
    // Sıralama numaralarını güncelle
    for (let i = 0; i < updatedSlides.length; i++) {
      updatedSlides[i].order_num = i + 1;
      await supabase.from("home_slides").update({ order_num: i + 1 }).eq("id", updatedSlides[i].id);
    }
    setSlides([...updatedSlides]);
  };

  const openDeleteDialog = (slide) => {
    setSlideToDelete({
      id: slide.id,
      title: slide.title
    });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!slideToDelete) return;
    
    await deleteSlide(slideToDelete.id);
    setDeleteDialogOpen(false);
    setSlideToDelete(null);
  };

  const deleteSlide = async (id: string | number) => {
    try {
      // Önce storage_path bilgisini al
      const { data: slideData, error: fetchError } = await supabase
        .from("home_slides")
        .select("storage_path")
        .eq("id", id)
        .single();
        
      if (fetchError) {
        console.error("Slayt bilgileri alınamadı:", fetchError);
        toast({ title: "Hata", description: "Slayt bilgileri alınamadı.", variant: "destructive" });
        return;
      }
      
      // S3'ten resmi sil
      if (slideData?.storage_path) {
        try {
          await deleteFileFromS3(slideData.storage_path);
          console.log("S3'ten resim silindi:", slideData.storage_path);
        } catch (s3Error) {
          console.error("S3 dosya silme hatası:", s3Error);
          // S3 silme hatası olsa bile işleme devam et
        }
      }
      
      // Supabase'den kaydı sil
      const { error } = await supabase.from("home_slides").delete().eq("id", id);
      if (error) {
        console.error("Supabase'den slayt silinemedi:", error);
        toast({ title: "Hata", description: "Slayt silinemedi.", variant: "destructive" });
        return;
      }
      
      setSlides(slides.filter((slide) => slide.id !== id));
      toast({ title: "Başarılı", description: "Slayt silindi." });
    } catch (error) {
      console.error("Slayt silme hatası:", error);
      toast({ title: "Hata", description: "Slayt silinemedi. Lütfen tekrar deneyin.", variant: "destructive" });
    }
  };

  return (
    <ContentLayout
      title="Anasayfa Slaytları"
      backLink="/dashboard"
      actionButton={{ label: "Yeni Slayt Ekle", href: "/content/home/slides/new" }}
    >
      <ScrollArea className="h-full max-h-[600px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Sıra</TableHead>
              <TableHead>Başlık</TableHead>
              <TableHead>Resim</TableHead>
              <TableHead className="w-[150px]">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4}>Slaytlar yükleniyor...</TableCell></TableRow>
            ) : slides.length === 0 ? (
              <TableRow><TableCell colSpan={4}>Hiç slayt yok.</TableCell></TableRow>
            ) : (
              slides.map((slide) => (
                <TableRow key={slide.id}>
                  <TableCell>{slide.order_num}</TableCell>
                  <TableCell className="font-medium">{slide.title}</TableCell>
                  <TableCell>
                    {slide.storage_path ? (
                      <div className="flex items-center">
                        <img 
                          src={slide.storage_path} 
                          alt={slide.title} 
                          className="h-12 w-24 object-cover rounded-md"
                        />
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Resim yok</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => moveSlide(slide.id, "up")}> <MoveUp className="h-4 w-4" /> </Button>
                      <Button variant="ghost" size="icon" onClick={() => moveSlide(slide.id, "down")}> <MoveDown className="h-4 w-4" /> </Button>
                      <Button variant="ghost" size="icon" asChild>
                        <a href={`/content/home/slides/${slide.id}/edit`}><Pencil className="h-4 w-4" /></a>
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(slide)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slaytı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{slideToDelete?.title}" başlıklı slaytı silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSlideToDelete(null)}>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ContentLayout>
  );
}
